import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { Col, Row, UncontrolledAlert, Spinner } from "reactstrap"
import api from "API/Api"
import { AvForm, AvField, AvRadioGroup, AvRadio } from "availity-reactstrap-validation"
import PageLoader from "./../../../assets/loading/pageLoad1.gif"

const AddCcPurchase = () => {
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [pageLoad, setPageLoad] = useState(true)
  const [centers, setCenters] = useState([])
  const [routes, setRoutes] = useState([])
  const [collections, setCollections] = useState([])
  const [vendors, setVendors] = useState([])
  const [routesLoading, setRoutesLoading] = useState(false)
  const [collectionsLoading, setCollectionsLoading] = useState(false)
  const [entryMode, setEntryMode] = useState("1") // Default to SNF mode

  // Quality parameter state for auto-calculation
  const [fat, setFat] = useState("")
  const [snf, setSnf] = useState("")
  const [clr, setClr] = useState("")

  useEffect(() => { getData() }, [])

  const getData = async () => {
    try {
      const [centersResp, vendorsResp] = await Promise.all([
        api.get("centers?per_page=500"),
        api.get("vendors?per_page=500")
      ])
      setCenters(Array.isArray(centersResp.data?.data) ? centersResp.data.data : centersResp.data?.data?.data || [])
      setVendors(Array.isArray(vendorsResp.data?.data) ? vendorsResp.data.data : vendorsResp.data?.data?.data || [])
    } catch (e) { console.error("Failed to load:", e) }
    setPageLoad(false)
  }

  const onCenterChange = async (value) => {
    setRoutes([]); setCollections([])
    if (!value) return
    setRoutesLoading(true)
    try {
      const resp = await api.get("route-cc/" + value)
      setRoutes(Array.isArray(resp.data?.data) ? resp.data.data : resp.data?.data?.data || [])
    } catch (e) { console.error(e) }
    setRoutesLoading(false)
  }

  const onRouteChange = async (value) => {
    setCollections([])
    if (!value) return
    setCollectionsLoading(true)
    try {
      const resp = await api.get("collection-center-route/" + value)
      setCollections(Array.isArray(resp.data?.data) ? resp.data.data : resp.data?.data?.data || [])
    } catch (e) { console.error(e) }
    setCollectionsLoading(false)
  }

  // Auto-calculate SNF or CLR based on entry mode
  // Dairy formula: SNF = (CLR / 4) + (0.21 × FAT) + 0.36
  //                CLR = (SNF - 0.36 - 0.21 × FAT) × 4
  const calcFromSnf = (fatVal, snfVal) => {
    const f = parseFloat(fatVal), s = parseFloat(snfVal)
    if (!isNaN(f) && !isNaN(s) && f > 0 && s > 0) {
      const c = ((s - 0.36 - (0.21 * f)) * 4).toFixed(2)
      setClr(c > 0 ? c : "0")
    }
  }

  const calcFromClr = (fatVal, clrVal) => {
    const f = parseFloat(fatVal), c = parseFloat(clrVal)
    if (!isNaN(f) && !isNaN(c) && f > 0 && c > 0) {
      const s = ((c / 4) + (0.21 * f) + 0.36).toFixed(2)
      setSnf(s > 0 ? s : "0")
    }
  }

  const onFatChange = (val) => {
    setFat(val)
    if (entryMode === "1" && snf) calcFromSnf(val, snf)
    if (entryMode === "2" && clr) calcFromClr(val, clr)
  }

  const onSnfChange = (val) => {
    setSnf(val)
    if (entryMode === "1" && fat) calcFromSnf(fat, val)
  }

  const onClrChange = (val) => {
    setClr(val)
    if (entryMode === "2" && fat) calcFromClr(fat, val)
  }

  const onEntryModeChange = (val) => {
    setEntryMode(val)
    // Recalculate when switching modes
    if (val === "1" && fat && snf) calcFromSnf(fat, snf)
    if (val === "2" && fat && clr) calcFromClr(fat, clr)
  }

  const handleValidSubmit = async (event, values) => {
    // Inject auto-calculated values
    if (entryMode === "1") values.purchaseClr = clr
    if (entryMode === "2") values.purchaseSnf = snf
    setLoading(true); setSubmitted(false)
    try {
      const resp = await api.post("create-cc-purchase", values)
      if (resp.data.success) { setSubmitted(true); setTimeout(() => setSubmitted(false), 4000) }
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  const today = new Date().toISOString().split("T")[0]

  if (pageLoad) return <div className="pageLoader"><img src={PageLoader} alt="Loading..." /></div>

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}><h4>Add CC Purchase</h4></Col>
          <Col md={6} style={{ textAlign:"right" }}><Link to="/cc-purchases" className="dashBtn"><i className="fas fa-chevron-left"></i> <b>Back</b></Link></Col>
        </Row>
      </div>
      <div className="dashContent">
        <AvForm className="form-horizontal" onValidSubmit={(e, v) => handleValidSubmit(e, v)}>
          <h6 style={{ color:"#6366f1", borderBottom:"2px solid #e2e8f0", paddingBottom:"8px", marginBottom:"16px" }}><i className="fas fa-map-marker-alt"></i> Location Details</h6>
          <Row>
            <Col md={6}><div className="mb-3">
              <AvField type="select" name="chillingCenter" label="Chilling Center" required onChange={e => onCenterChange(e.target.value)}>
                <option value="">Select Chilling Center</option>
                {centers.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}
              </AvField>
            </div></Col>
            <Col md={6}><div className="mb-3">
              <AvField type="select" name="route" label={routesLoading?"Loading Routes...":"Route"} required onChange={e => onRouteChange(e.target.value)}>
                <option value="">{routesLoading?"Loading...":"Select Route"}</option>
                {routes.map((r, i) => <option key={i} value={r.id}>{r.routeName}</option>)}
              </AvField>
            </div></Col>
            <Col md={6}><div className="mb-3">
              <AvField type="select" name="collectionCenter" label={collectionsLoading?"Loading...":"Collection Center"} required>
                <option value="">{collectionsLoading?"Loading...":"Select Collection Center"}</option>
                {collections.map((c, i) => <option key={i} value={c.id}>{c.centerName}</option>)}
              </AvField>
            </div></Col>
            <Col md={6}><div className="mb-3">
              <AvField type="select" name="vendor" label="Vendor">
                <option value="">Select Vendor (Optional)</option>
                {vendors.map((v, i) => <option key={i} value={v.id}>{v.vendorName || v.name}</option>)}
              </AvField>
            </div></Col>
          </Row>

          <h6 style={{ color:"#6366f1", borderBottom:"2px solid #e2e8f0", paddingBottom:"8px", marginBottom:"16px", marginTop:"8px" }}><i className="fas fa-shopping-cart"></i> Purchase Details</h6>
          <Row>
            <Col md={4}><div className="mb-3"><AvField name="purchaseDate" label="Purchase Date" type="date" value={today} required /></div></Col>
            <Col md={4}><div className="mb-3"><AvRadioGroup inline name="shift" label="Shift" required errorMessage="Pick one!"><AvRadio label="AM (Morning)" value="0" /><AvRadio label="PM (Evening)" value="1" /></AvRadioGroup></div></Col>
            <Col md={4}><div className="mb-3"><AvField name="serialNo" label="Serial No" type="text" /></div></Col>
            <Col md={4}><div className="mb-3"><AvRadioGroup inline name="milkType" label="Milk Type" required errorMessage="Pick one!"><AvRadio label="Cow" value="1" /><AvRadio label="Buffalo" value="2" /><AvRadio label="Common" value="3" /></AvRadioGroup></div></Col>
            <Col md={4}><div className="mb-3"><AvField name="qtyUnit" label="Qty Unit" type="select"><option value="Ltrs">Liters</option><option value="KG">Kilograms</option></AvField></div></Col>
            <Col md={4}><div className="mb-3"><AvField name="purchaseTime" label="Purchase Time" type="time" /></div></Col>
          </Row>

          <h6 style={{ color:"#6366f1", borderBottom:"2px solid #e2e8f0", paddingBottom:"8px", marginBottom:"16px", marginTop:"8px" }}><i className="fas fa-flask"></i> Quality Parameters</h6>

          {/* Entry Mode selector with explanation */}
          <Row className="mb-3">
            <Col md={6}>
              <AvRadioGroup inline name="entryMode" label="Entry Mode" value="1" required errorMessage="Pick one!" onChange={e => onEntryModeChange(e.target.value)}>
                <AvRadio label="Enter SNF (auto-calculate CLR)" value="1" />
                <AvRadio label="Enter CLR (auto-calculate SNF)" value="2" />
              </AvRadioGroup>
              <small className="text-muted">
                {entryMode === "1" ? "📝 Enter FAT + SNF → CLR will be calculated automatically" : "📝 Enter FAT + CLR → SNF will be calculated automatically"}
              </small>
            </Col>
          </Row>

          <Row>
            <Col md={3}><div className="mb-3"><AvField name="qtyLtrs" label="Quantity (Liters)" type="number" step="0.01" required validate={{min:{value:0}}} /></div></Col>
            <Col md={3}><div className="mb-3"><AvField name="qtyKg" label="Quantity (KG)" type="number" step="0.01" validate={{min:{value:0}}} /></div></Col>
            <Col md={3}><div className="mb-3">
              <AvField name="purchaseFat" label="FAT" type="number" step="0.01" required
                validate={{min:{value:0},max:{value:15}}}
                value={fat} onChange={e => onFatChange(e.target.value)} />
            </div></Col>
            <Col md={3}><div className="mb-3">
              <AvField name="purchaseSnf" label={entryMode==="2" ? "SNF (Auto-calculated)" : "SNF"}
                type="number" step="0.01" required
                readOnly={entryMode==="2"}
                style={entryMode==="2" ? {backgroundColor:"#e8f5e9",fontWeight:"700"} : {}}
                validate={{min:{value:0},max:{value:15}}}
                value={snf} onChange={e => onSnfChange(e.target.value)} />
            </div></Col>
            <Col md={3}><div className="mb-3">
              <AvField name="purchaseClr" label={entryMode==="1" ? "CLR (Auto-calculated)" : "CLR"}
                type="number" step="0.01" required
                readOnly={entryMode==="1"}
                style={entryMode==="1" ? {backgroundColor:"#e8f5e9",fontWeight:"700"} : {}}
                value={clr} onChange={e => onClrChange(e.target.value)} />
            </div></Col>
            <Col md={3}><div className="mb-3"><AvField name="waterPercent" label="Water %" type="number" step="0.01" validate={{min:{value:0},max:{value:100}}} /></div></Col>
            <Col md={3}><div className="mb-3"><AvField name="purchaseCans" label="Cans" type="number" step="1" validate={{min:{value:0}}} /></div></Col>
            <Col md={3}><div className="mb-3"><AvField name="temperature" label="Temperature" type="number" step="0.1" /></div></Col>
          </Row>

          <h6 style={{ color:"#6366f1", borderBottom:"2px solid #e2e8f0", paddingBottom:"8px", marginBottom:"16px", marginTop:"8px" }}><i className="fas fa-rupee-sign"></i> Pricing</h6>
          <Row>
            <Col md={3}><div className="mb-3"><AvField name="ratePerLtr" label="Rate/Ltr (₹)" type="number" step="0.01" validate={{min:{value:0}}} /></div></Col>
            <Col md={3}><div className="mb-3"><AvField name="ratePerKg" label="Rate/KG (₹)" type="number" step="0.01" validate={{min:{value:0}}} /></div></Col>
            <Col md={3}><div className="mb-3"><AvField name="incentive" label="Incentive (₹)" type="number" step="0.01" value="0" validate={{min:{value:0}}} /></div></Col>
            <Col md={3}><div className="mb-3"><AvField name="deductions" label="Deductions (₹)" type="number" step="0.01" value="0" validate={{min:{value:0}}} /></div></Col>
            <Col md={4}><div className="mb-3"><AvField name="netAmount" label="Net Amount (₹)" type="number" step="0.01" validate={{min:{value:0}}} /></div></Col>
            <Col md={8}><div className="mb-3"><AvField name="remarks" label="Remarks" type="textarea" rows="2" /></div></Col>
          </Row>

          <div className="mb-3 row mt-4">
            <div className="col-6">{submitted && <UncontrolledAlert color="success"><strong>CC Purchase Added Successfully!</strong></UncontrolledAlert>}</div>
            <div className="col-6 text-end">
              <button className="btn btn-primary w-md waves-effect waves-light" type="submit" disabled={loading}>
                {loading ? <><Spinner animation="border" size="sm" /><span style={{paddingLeft:"10px"}}>Saving...</span></> : <span><i className="fas fa-save"></i> Save Purchase</span>}
              </button>
            </div>
          </div>
        </AvForm>
      </div>
    </div>
  )
}

export default AddCcPurchase
